import { createSelector, createSlice } from "@reduxjs/toolkit";

import { getProjectCurrentUsage } from "Api/billingSystem/costs/getProjectCurrentUsage.request";
import {
  getBillingErrorStatus,
  getNewBillingSystemError
} from "Hooks/billingSystem/getNewBillingSystemError";
import logger from "Libs/logger";
import { setDeep } from "Libs/objectAccess";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import type {
  ProjectCurrentUsage,
  ProjectCurrentUsageItem
} from "Api/billingSystem/types";
import type { RootState } from "Store/configureStore";

// Expected outcomes that should not be reported to Sentry as noise: a 404 when
// the project was deleted or is not yet provisioned, and a 403 for project
// members who are not users on the billing profile.
const isExpectedError = (err: unknown): boolean => {
  const status = getBillingErrorStatus(err);
  return status === 403 || status === 404;
};

// Thunk
export const getProjectCurrentUsageThunk = createAppAsyncThunk(
  "billingSystem/projectCurrentUsage/get",
  async (
    {
      billingProfileId,
      projectId
    }: {
      billingProfileId: string;
      projectId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await getProjectCurrentUsage({ billingProfileId, projectId });
    } catch (err) {
      if (!isExpectedError(err)) {
        logger(err, {
          action: "get_project_current_usage",
          billingProfileId,
          projectId
        });
      }
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type ProjectCurrentUsageState = {
  dataByProjectId: {
    [projectId: string]: {
      usage?: ProjectCurrentUsage;
      loading: boolean;
      loaded: boolean;
      error?: string;
    };
  };
};

const initialState: ProjectCurrentUsageState = {
  dataByProjectId: {}
};

// Slice
const projectCurrentUsageSlice = createSlice({
  name: "projectCurrentUsage",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getProjectCurrentUsageThunk.pending, (state, { meta }) => {
        const { projectId } = meta.arg;
        setDeep(state, ["dataByProjectId", projectId, "loading"], true);
        setDeep(state, ["dataByProjectId", projectId, "error"], undefined);
      })
      .addCase(
        getProjectCurrentUsageThunk.fulfilled,
        (state, { meta, payload }) => {
          const { projectId } = meta.arg;
          setDeep(state, ["dataByProjectId", projectId, "loading"], false);
          setDeep(state, ["dataByProjectId", projectId, "loaded"], true);
          setDeep(state, ["dataByProjectId", projectId, "usage"], payload);
        }
      )
      .addCase(
        getProjectCurrentUsageThunk.rejected,
        (state, { meta, payload }) => {
          const { projectId } = meta.arg;
          setDeep(state, ["dataByProjectId", projectId, "loading"], false);
          setDeep(state, ["dataByProjectId", projectId, "loaded"], true);
          setDeep(
            state,
            ["dataByProjectId", projectId, "error"],
            payload?.error
          );
        }
      );
  }
});

export default projectCurrentUsageSlice.reducer;

// Selectors
const projectCurrentUsageStateSelector = (state: RootState) =>
  state.projectCurrentUsage;

export const selectProjectCurrentUsage = createSelector(
  projectCurrentUsageStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]?.usage
);

export const selectProjectCurrentUsageLoading = createSelector(
  projectCurrentUsageStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]?.loading ?? false
);

export const selectProjectCurrentUsageLoaded = createSelector(
  projectCurrentUsageStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]?.loaded ?? false
);

export const selectProjectCurrentUsageError = createSelector(
  projectCurrentUsageStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]?.error
);

export type TrafficUsageKey =
  | "egress_bandwidth"
  | "ingress_requests"
  | "fastly_bandwidth"
  | "fastly_requests";

export type ProjectTrafficUsage = Partial<
  Record<TrafficUsageKey, ProjectCurrentUsageItem>
>;

// The endpoint keys usage by catalog product name; the traffic block wants the
// same four measures the legacy subscription usage exposes.
const TRAFFIC_PRODUCTS: Record<string, TrafficUsageKey> = {
  "Origin Bandwidth": "egress_bandwidth",
  "Origin Requests": "ingress_requests",
  "Fastly CDN Bandwidth": "fastly_bandwidth",
  "Fastly CDN Requests": "fastly_requests"
};

export const selectProjectTrafficUsage = createSelector(
  selectProjectCurrentUsage,
  (usage): ProjectTrafficUsage | undefined => {
    if (!usage?.items) return undefined;

    const traffic: ProjectTrafficUsage = {};
    usage.items.forEach(item => {
      const key = TRAFFIC_PRODUCTS[item.product_name];
      if (key) traffic[key] = item;
    });

    return Object.keys(traffic).length > 0 ? traffic : undefined;
  }
);
